import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, Tooltip } from "@mui/material";
import { ConnectionDto, ConnectionName } from "@arelia-types/connection";
import { PropsWithChildren, useEffect, useState, useContext } from "react";
import AccountsForm from "../AccountsForm";
import { useDeleteConnectionMutation, useSaveConnection } from "../../../../api/connections";
import { UseQueryResult } from "react-query";
import useEventListeners from "@arelia-hooks/useEventListeners";
import { authWindowName } from "../constants";
import LogoutIcon from "@mui/icons-material/Logout";
import EditIcon from "@mui/icons-material/Edit";
import { getPostForMeAuthUrl } from "../../../social/post/util";
import openNewWindow, { waitForWindowClose } from "@arelia-utils/helpers/openNewWindow";
import { fetchAccounts, disconnectAllPlatform } from "../../../social/post/util";
import { PostForMeAccount, SocialPlatform } from "src/features/social/post/models";
import { useConfiguration } from "src/api/configuration";
import AppContext from "src/AppContext";
import { SubscriptionPlanName } from "@arelia-types/subscriptions";

type Props = {
  connection: ConnectionDto,
  title?: string,
  showSaveButton?: boolean,
  canDisconnect?: boolean,
  pagesQuery?: UseQueryResult<any[], unknown>,
  renderActions?: () => "" | JSX.Element,
  onClose: () => void,
  onPostForMeChange?: () => void,
}

export default function BasicDialog({
  connection,
  title = `${connection.name} connection`,
  showSaveButton = false,
  pagesQuery,
  renderActions,
  canDisconnect,
  onClose,
  onPostForMeChange,
  children
}: PropsWithChildren<Props>) {
  const DISABLE_CONNECTION = connection.maintenanceState === "disabled";
  const { mutate: saveConnection } = useSaveConnection();
  const [selectedAccounts, setSelectedAccounts] = useState<{ id: string; name: string; isPostingEnabled: boolean }[]>(connection.accounts ?? []);
  const { mutate: deleteConnection } = useDeleteConnectionMutation();
  const [enablePostingError, setEnablePostingError] = useState<string | null>(null);
  const [postForMeAccounts, setPostForMeAccounts] = useState<PostForMeAccount[] | null>(null);
  const [togglingPosting, setTogglingPosting] = useState(false);
  const { data: configuration } = useConfiguration();
  const { account } = useContext(AppContext);

  const isPostingFeatureEnabled = configuration?.flags?.EnablePosting === true;
  const planSupportsMySocial = [SubscriptionPlanName.MySocial, SubscriptionPlanName.MySocialMyFans].includes(account?.plan as SubscriptionPlanName);

  const isPostForMePlatform =
    connection.name === ConnectionName.facebook ||
    connection.name === ConnectionName.instagram ||
    connection.name === ConnectionName.tiktok;

  useEffect(() => {
    (async () => {
      setPostForMeAccounts(await fetchAccounts());
    })();
  }, [connection.accounts]);

  useEffect(() => {
    if (connection.accounts) setSelectedAccounts(connection.accounts);
  }, [connection.accounts]);

  useEffect(() => {
    if (!postForMeAccounts || postForMeAccounts.length === 0) return;

    const platform = connection.name.toLowerCase();

    setSelectedAccounts(prev =>
      prev.map(acc => {
        const accId = (acc.id ?? "").trim();
        const accName = (acc.name ?? "").trim().toLowerCase();

        // For Instagram connected via Facebook, also check if the account username matches
        const match = postForMeAccounts.find(a => {
          const pfmPlatform = (a.platform ?? "").toLowerCase();
          const pfmUsername = (a.username ?? "").trim().toLowerCase();
          const pfmUserId = a.user_id ?? "";

          // Check platform match - for Instagram, the PostForMe platform should be "instagram"
          const platformMatches = pfmPlatform === platform;

          // Check if user ID or username matches
          const userMatches =
            (!!pfmUserId && pfmUserId === accId) ||
            (pfmUsername === accName);

          return platformMatches && userMatches;
        });

        if (!match) {
          return acc;
        }

        const isConnected = (match.status ?? "").toLowerCase() === "connected";
        if (acc.isPostingEnabled === isConnected) return acc;
        return { ...acc, isPostingEnabled: isConnected };
      })
    );
  }, [postForMeAccounts, connection.name]);

  useEffect(() => {
    if (connection.accounts) setSelectedAccounts(connection.accounts);
  }, [connection.accounts]);

  const onSave = () => {
    saveConnection({
      connection: connection.name,
      values: { accounts: selectedAccounts }
    });
    onClose();
  };

  const enablePosting = async () => {
    if (!isPostForMePlatform) return;
    setEnablePostingError(null);
    setTogglingPosting(true);
    try {
      const platform = connection.name.toLowerCase();
      const connectionType = platform === SocialPlatform.Instagram ? SocialPlatform.Facebook : platform;
      const url = await getPostForMeAuthUrl(platform, connectionType);
      const win = openNewWindow(url, {
        width: 650,
        height: 750,
        left: 'center',
        top: 'center',
        resizable: 1,
        scrollbars: 1
      });
      await waitForWindowClose(win);
      setPostForMeAccounts(await fetchAccounts());
    } catch (e: any) {
      setEnablePostingError(e.message ?? "Failed to enable posting");
    } finally {
      setTogglingPosting(false);
    }
  };

  const disablePosting = async () => {
    if (!isPostForMePlatform) return;
    setEnablePostingError(null);
    setTogglingPosting(true);
    try {
      const platform = connection.name.toLowerCase();
      await disconnectAllPlatform(platform);
      setPostForMeAccounts(await fetchAccounts());
    } catch (e: any) {
      setEnablePostingError(e.message ?? "Failed to disable posting");
    } finally {
      setTogglingPosting(false);
    }
  };

  const isPostingEnabled =
    !!postForMeAccounts?.some(
      a =>
        a.platform?.toLowerCase() === connection.name.toLowerCase() &&
        (a.status ?? "").toLowerCase() === "connected"
    );

  useEventListeners(["message"], (e: any) => {
    if (e && e.source.name === authWindowName && e.data.reload && pagesQuery) {
      pagesQuery.refetch();
    }
  });

  return (
    <Dialog
      open={true}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="alert-dialog-title">{title}</DialogTitle>

      <DialogContent dividers>
        {DISABLE_CONNECTION && (
          <Typography>
            Please enjoy this instrumental break as we are upgrading this connection.
          </Typography>
        )}
        {!DISABLE_CONNECTION && children}
        {connection.connected && pagesQuery && (
          <AccountsForm
            selectedAccounts={selectedAccounts}
            setSelectedAccounts={setSelectedAccounts}
            pagesQuery={pagesQuery}
          />
        )}

        {canDisconnect && connection.connected && (
          <Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              startIcon={<LogoutIcon />}
              variant="contained"
              color="error"
              onClick={async () => {
                if (isPostingEnabled) {
                  try {
                    await disconnectAllPlatform(connection.name.toLowerCase());
                    // Force refresh of parent state immediately after disconnect
                    if (onPostForMeChange) {
                      onPostForMeChange();
                    }
                  } catch (e) {
                    console.error("Failed to disconnect PostForMe on logout", e);
                  }
                }
                deleteConnection(connection.name);
              }}
            >
              Log Out (Disconnect)
            </Button>
            {isPostForMePlatform && isPostingFeatureEnabled && (
              <Tooltip title={!planSupportsMySocial ? "You need to upgrade your subscription to My Social or My Social + My Fans" : ""}>
                <span>
                  <Button
                    startIcon={<EditIcon />}
                    variant="contained"
                    color={isPostingEnabled ? "error" : "primary"}
                    onClick={isPostingEnabled ? disablePosting : enablePosting}
                    disabled={togglingPosting || !planSupportsMySocial}
                  >
                    {isPostingEnabled ? "Disable Posting" : "Enable Posting"}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
        {enablePostingError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 1, display: "block" }}
          >
            {enablePostingError}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between" }}>
        {!DISABLE_CONNECTION && renderActions && renderActions()}
        <Box sx={{ ml: "auto" }}>
          {showSaveButton && !DISABLE_CONNECTION && (
            <Button
              variant="contained"
              sx={{ mr: "16px", width: "100px" }}
              onClick={onSave}
            >
              Save
            </Button>
          )}
          <Button
            variant="text"
            color="inherit"
            onClick={onClose}
            sx={{ width: "100px" }}
          >
            Close
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
